import { useContext, useState } from 'react';
import { Grid, Box, Typography, Paper, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttLabelForm,
    SttFormText,
    SttFormTextarea,
    SttFormPrice,
} from '../../../sporttia/all';
import { SttChipRate } from '../../../sporttia/chips/SttChipRate';
import { fullName, formatPriceByLocale } from '../../../lib/utils';
import DateBuilder from '../../../lib/DateBuilder';
import { Booking } from './BookingDialog';
import BookingDialogOccupants from './BookingDialogOccupants';
import Mship from '../../../pages/sc/mships/Mship';

type FormChange = {
    name: string | undefined;
    value: unknown;
};

type BookingDetailDialogProps = {
    booking: Booking;
    setBooking: React.Dispatch<React.SetStateAction<Booking | null>>;
    onUpdate?: (booking?: Booking) => void;
    onLastOccupantDeleted?: () => void;
};

export default function BookingDetailDialog({
    booking,
    setBooking,
    onUpdate,
    onLastOccupantDeleted,
}: BookingDetailDialogProps) {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const [selectedMshipId, setSelectedMshipId] = useState<number | null>(null);

    /**
     * Handle form
     */
    const handleForm = ({ name, value }: FormChange) => {
        if (!name) return;
        setBooking((prev: any) => {
            if (!prev) return null;
            return { ...prev, [name]: value };
        });
    };

    // Algo raro pasa
    if (!booking.user) {
        return null;
    }

    // -----| Render |-----
    return (
        <Grid container spacing={3}>
            {/* Left column - Booking details */}
            <Grid size={{ xs: 12, md: 7 }}>
                <Grid container spacing={3}>
                    <SttLabelForm
                        grid
                        title="ID"
                        md={4}
                        text={String(booking.id)}
                    />

                    <SttLabelForm
                        grid
                        title={t('Created')}
                        md={4}
                        text={new DateBuilder(
                            booking.created,
                            cxt?.sc?.timezone?.name,
                            cxt?.lang?.key,
                        ).dmyhm()}
                    />

                    <SttLabelForm
                        grid
                        title={t('Operator')}
                        md={4}
                        text={fullName(
                            booking.creator as Parameters<typeof fullName>[0],
                        )}
                    />

                    <SttLabelForm
                        grid
                        title={t('Facility')}
                        md={4}
                        text={booking.facility.name}
                    />

                    <SttLabelForm
                        grid
                        title={t('Date')}
                        md={4}
                        text={new DateBuilder(
                            booking.ini,
                            cxt?.sc?.timezone?.name,
                            cxt?.lang?.key,
                        ).dmy()}
                    />

                    <SttLabelForm
                        grid
                        title={t('Time')}
                        md={4}
                        text={`${new DateBuilder(
                            booking.ini,
                            cxt?.sc?.timezone?.name,
                            cxt?.lang?.key,
                        ).hm()} - ${new DateBuilder(
                            booking.end,
                            cxt?.sc?.timezone?.name,
                            cxt?.lang?.key,
                        ).hm()}`}
                    />
                    <SttLabelForm
                        grid
                        title={t('PaymentStatus')}
                        md={4}
                        text={`${t('PS.NOT_PAID')} ${formatPriceByLocale(
                            (booking?.totalBill ?? 0) -
                                (booking?.totalPaid ?? 0),
                            booking?.priceShape?.currency ?? undefined,
                            booking?.priceShape?.locale,
                        )} / ${t('PS.PAID')}: ${formatPriceByLocale(
                            booking?.totalPaid,
                            booking?.totalPaidShape?.currency ?? undefined,
                            booking?.totalPaidShape?.locale,
                        )}`}
                    />

                    {booking.cancelator && (
                        <SttLabelForm
                            grid
                            title={t('Cancelator')}
                            md={4}
                            text={
                                booking.cancelator
                                    ? booking.cancelator.fullName
                                    : ''
                            }
                        />
                    )}

                    {booking.user && booking.user.mship ? (
                        <Grid size={{ xs: 12, md: booking.cancelator ? 4 : 8 }}>
                            <SttLabelForm
                                title={t('User')}
                                text={
                                    <Link
                                        onClick={() =>
                                            setSelectedMshipId(
                                                booking.user.mship!.id,
                                            )
                                        }
                                        sx={{
                                            color: 'var(--brand-text)',
                                            textDecoration: 'none',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        {booking.user.mship.name}
                                    </Link>
                                }
                            />
                        </Grid>
                    ) : (
                        <SttFormText
                            disabled
                            grid
                            md={booking.cancelator ? 4 : 8}
                            name="user.name"
                            caption={t('User')}
                            defVal={booking.user.name}
                            onChange={handleForm}
                        />
                    )}
                    <SttFormPrice
                        grid
                        xs={12}
                        sm={12}
                        md={4}
                        name="price"
                        caption={t('Price')}
                        defVal={String(booking.price)}
                        onChange={handleForm}
                    />

                    <SttFormTextarea
                        grid
                        name="notes"
                        caption={t('Notes')}
                        defVal={booking.notes}
                        onChange={handleForm}
                    />

                    {booking.rate && booking.rate.length > 0 && (
                        <SttChipRate
                            grid
                            xs={12}
                            rates={booking.rate}
                            title={t('Rates')}
                        />
                    )}
                </Grid>
            </Grid>

            {/* Right column - Occupants */}
            <Grid size={{ xs: 12, md: 5 }}>
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        height: '100%',
                        bgcolor: 'background.default',
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{ mb: 1 }}
                    >
                        {t('Occupants')}
                    </Typography>
                    <Box>
                        <BookingDialogOccupants
                            booking={booking}
                            onUpdate={onUpdate}
                            onLastOccupantDeleted={
                                onLastOccupantDeleted || (() => {})
                            }
                        />
                    </Box>
                </Paper>
            </Grid>

            {selectedMshipId && (
                <Mship
                    id={selectedMshipId}
                    open
                    onClose={() => setSelectedMshipId(null)}
                    disableUrlNavigation
                />
            )}
        </Grid>
    );
}
